import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Grid,
  Button,
  CssBaseline,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Close } from "@mui/icons-material";
import { tokens } from "../../../theme/theme";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import CallAlertBar from "../../../Layout/Alertbar";
import AddUpdateOT from "./AddUpdateOT";
import CustomTableContainer from "../../../components/table/CustomTableContainer";

const OTIndex = ({ handleBackWinderMachineEntry }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [otList, setOtList] = useState([]);
  const [filteredOtList, setFilteredOtList] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOT, setSelectedOT] = useState(null);

  const [filters, setFilters] = useState({
    ot_code: "",
    ot_name: "",
    state_name: "",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuText, setMenuText] = useState("All OT Rules");

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");

  useEffect(() => {
    loadOTList();
  }, []);

  const loadOTList = () => {
    callApi(
      API_ENDPOINTS.MASTER_GET_OVERTIME_MASTER_RULES_LIST,
      "POST",
      { "Content-Type": "application/json" },
      { p_company_id: 13, p_plant_id: 1 }
    )
      .then((data) => {
        setOtList(data || []);
        setFilteredOtList(data || []);
      })
      .catch(() => {
        setAlertSeverity("error");
        setAlertTitle("Error");
        setAlertMessage("Unable to load OT details");
        setAlertOpen(true);
      });
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuItemClick = (option) => {
    setMenuText(option);
    handleMenuClose();

    if (option === "All OT Rules") setFilteredOtList(otList);
    else if (option === "Active OT Rules")
      setFilteredOtList(
        otList.filter((x) => x.status === true || x.status === "Active")
      );
    else if (option === "Inactive OT Rules")
      setFilteredOtList(
        otList.filter((x) => x.status === false || x.status === "Inactive")
      );
  };

  useEffect(() => {
    const filtered = otList.filter((row) => {
      return (
        (row.ot_code || "")
          .toLowerCase()
          .includes(filters.ot_code.toLowerCase()) &&
        (row.ot_name || "")
          .toLowerCase()
          .includes(filters.ot_name.toLowerCase()) &&
        (row.state_name || "")
          .toLowerCase()
          .includes(filters.state_name.toLowerCase())
      );
    });
    setFilteredOtList(filtered);
  }, [filters, otList]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOT = () => {
    setSelectedOT(null);
    setIsFormOpen(true);
  };

  const handleEditOT = (ot) => {
    setSelectedOT(ot);
    setIsFormOpen(true);
  };

  const handleBack = () => handleBackWinderMachineEntry();

  return (
    <Grid>
      <CssBaseline />

      {/* AppBar with Title, Filter, New and Close */}
      {!isFormOpen && (
        <AppBar position="static" sx={{ backgroundColor: "inherit" }}>
          <Toolbar>
            <Typography variant="h5">Overtime Master</Typography>

            {/* Menu Filter */}
            <Button
              color="secondary"
              onClick={handleMenuClick}
              endIcon={<ExpandMoreIcon />}
              sx={{ ml: 2, textTransform: "none" }}
            >
              {menuText}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleMenuItemClick("All OT Rules")}>
                All OT Rules
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick("Active OT Rules")}>
                Active OT Rules
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick("Inactive OT Rules")}>
                Inactive OT Rules
              </MenuItem>
            </Menu>

            {/* Right buttons */}
            <Button
              size="small"
              variant="contained"
              color="secondary"
              sx={{ ml: "auto", textTransform: "none" }}
              onClick={handleAddOT}
            >
              New OT
            </Button>

            <IconButton onClick={handleBack}>
              <Close sx={{ fontSize: 18, color: "#2196F3" }} />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Alert */}
      <CallAlertBar
        parentRollMsgOpen={alertOpen}
        setParentRollMsgOpen={setAlertOpen}
        parentRollSeverityvalue={alertSeverity}
        setParentRollSeverityvalue={setAlertSeverity}
        parentRollAlertTitleValue={alertTitle}
        setParentRollAlertTitleValue={setAlertTitle}
        parentRollMessageValue={alertMessage}
        setParentRollMessageValue={setAlertMessage}
      />

      {/* Table */}
      {!isFormOpen && (
        <Box p={1}>
          <CustomTableContainer colors={colors}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>OT CODE</TableCell>
                    <TableCell>OT NAME</TableCell>
                    <TableCell>STATE</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search OT Code"
                        value={filters.ot_code}
                        onChange={(e) =>
                          handleFilterChange("ot_code", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search OT Name"
                        value={filters.ot_name}
                        onChange={(e) =>
                          handleFilterChange("ot_name", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search State"
                        value={filters.state_name}
                        onChange={(e) =>
                          handleFilterChange("state_name", e.target.value)
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredOtList.map((ot) => (
                    <TableRow
                      key={ot.ot_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleEditOT(ot)}
                    >
                      <TableCell sx={{ color: "teal" }}>{ot.ot_code}</TableCell>
                      <TableCell>{ot.ot_name}</TableCell>
                      <TableCell>{ot.state_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTableContainer>
        </Box>
      )}

      {/* Add / Edit OT Form */}
      {isFormOpen && (
        <AddUpdateOT
          selectedOT={selectedOT}
          onClose={() => setIsFormOpen(false)}
          refreshOTList={loadOTList}
        />
      )}
    </Grid>
  );
};

export default OTIndex;
